import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Target, Plus, CheckCircle2, Trash2, Users, User, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Goals() {
  const { user, refresh } = useAuth();
  const [goals, setGoals] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", points_reward: 100, scope: "individual", category: "Vendas" });

  const canManage = user.role === "admin" || user.role === "agency_manager";

  const load = () => api.get("/goals").then((r) => setGoals(r.data));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/goals/${id}/complete`);
      toast.success(`+${data.points_awarded} pontos! 🎉`);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(null); }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/goals", { ...form, points_reward: Number(form.points_reward) });
      toast.success("Objetivo criado!");
      setOpen(false);
      setForm({ title: "", description: "", points_reward: 100, scope: "individual", category: "Vendas" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    toast.success("Objetivo removido");
    await load();
  };

  if (!goals) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl">Objetivos</h1>
          <p className="text-brand-muted text-sm mt-1">Conclui objetivos para ganhar pontos e subir de nível.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-goal-button" className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
                <Plus size={16} className="mr-1" /> Novo objetivo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-brand-surface border-brand-border">
              <DialogHeader><DialogTitle>Criar objetivo</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label>Título</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
                    data-testid="goal-title" className="mt-1 bg-background border-brand-border rounded-sm" />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required
                    data-testid="goal-description" className="mt-1 bg-background border-brand-border rounded-sm" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Pontos</Label>
                    <Input type="number" value={form.points_reward} onChange={(e) => setForm({ ...form, points_reward: e.target.value })}
                      data-testid="goal-points" className="mt-1 bg-background border-brand-border rounded-sm" />
                  </div>
                  <div>
                    <Label>Âmbito</Label>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                      <SelectTrigger className="mt-1 bg-background border-brand-border rounded-sm" data-testid="goal-scope"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="team">Equipa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-1 bg-background border-brand-border rounded-sm" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-goal-button"
                    className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar objetivo"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {goals.map((g) => (
          <div key={g.id} data-testid="goal-card"
            className={`p-5 rounded-sm border transition-all ${g.completed ? "border-brand-success/40 bg-brand-success/5" : "border-brand-border bg-brand-surface hover:border-brand-blue/40"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className={`h-10 w-10 rounded-sm flex items-center justify-center shrink-0 ${g.scope === "team" ? "bg-brand-orange/15 text-brand-orange" : "bg-brand-blue/15 text-brand-blue"}`}>
                {g.scope === "team" ? <Users size={18} /> : <User size={18} />}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-brand-orange">+{g.points_reward}</span>
                {canManage && (
                  <button onClick={() => remove(g.id)} className="text-brand-muted hover:text-destructive" data-testid="delete-goal">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
            <h3 className="font-heading font-semibold mt-3">{g.title}</h3>
            <p className="text-sm text-brand-muted mt-1 leading-relaxed">{g.description}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs px-2 py-0.5 rounded-sm bg-brand-border/50 text-brand-muted">{g.category}</span>
              <span className="text-xs px-2 py-0.5 rounded-sm bg-brand-border/50 text-brand-muted">{g.scope === "team" ? "Equipa" : "Individual"}</span>
            </div>
            <div className="mt-4">
              {g.completed ? (
                <div className="flex items-center gap-2 text-sm text-brand-success font-medium">
                  <CheckCircle2 size={16} /> Concluído
                </div>
              ) : user.role === "admin" ? (
                <p className="text-xs text-brand-muted">Objetivo para consultores</p>
              ) : (
                <Button onClick={() => complete(g.id)} disabled={busy === g.id} data-testid="complete-goal-button"
                  className="w-full bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
                  {busy === g.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Marcar como concluído"}
                </Button>
              )}
            </div>
          </div>
        ))}
        {goals.length === 0 && (
          <div className="col-span-full text-center py-16 text-brand-muted">
            <Target size={40} className="mx-auto mb-3 opacity-40" />
            Ainda não há objetivos.
          </div>
        )}
      </div>
    </div>
  );
}
